import { LogOut, X } from "lucide-react";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

import { Logo } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useAuthStore } from "@/stores/authStore";

import { NAV_ROUTES, findRoute, routePermission } from "../routes";
import { SidebarSummary } from "./SidebarSummary";

export interface SidebarProps {
  /** Whether the drawer is showing. Ignored at `lg` and above, where the rail is static. */
  open?: boolean;
  /** Close the drawer. Called on Escape, on the backdrop, and after navigating. */
  onClose?: () => void;
}

/**
 * The dark navigation rail from both designs — a **static rail** from `lg` up
 * and an **off-canvas drawer** below it.
 *
 * One component rather than two, because the contents are identical and a phone
 * navigation that had drifted from the desktop one is how a route ends up
 * reachable on a laptop and invisible on a till. What changes is only where the
 * element sits: `lg:static` puts it back in the flex row, and `lg:translate-x-0`
 * cancels the transform that hides it.
 *
 * Structure, top to bottom: logo lockup, navigation, a contextual summary panel,
 * and Logout pinned to the bottom.
 */
export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = findRoute(pathname);
  const can = useAuthStore((s) => s.can);
  const logout = useAuthStore((s) => s.logout);

  // Hiding a screen someone cannot open is a courtesy, not a security control —
  // the server checks the same permission on every request. A cashier who
  // navigated here by URL would see an explanation, not data.
  const inSupportView = useAuthStore((s) => Boolean(s.session?.supportView));
  const visibleRoutes = NAV_ROUTES.filter((route) => {
    const permission = routePermission(route, inSupportView);
    return !permission || can(permission);
  });

  const panel = useRef<HTMLDivElement>(null);

  // Memoised, and it has to be. `onClose ?? (() => {})` inline would be a new
  // function on every render, so the trap's effect would tear down and set up
  // again each time — and its teardown restores focus to whatever opened the
  // drawer. The drawer would open, focus its first link, and immediately hand
  // focus back, which is a bug you cannot see in jsdom and can only find by
  // opening the thing in a browser.
  const close = useCallback(() => onClose?.(), [onClose]);

  // Only while it is a drawer. Trapping focus in a rail that is permanently on
  // screen at desktop width would strand a keyboard user in the navigation.
  //
  // `lockScroll: false` for the same reason: at `lg` and above nothing is
  // covering the page, and freezing the body there would be a bug that only
  // showed up on the machines nobody tests on.
  useFocusTrap({ active: open, panel, onClose: close, lockScroll: false });

  return (
    <aside
      ref={panel}
      id="primary-navigation"
      // `fixed` and translated off-canvas below `lg`; back in the flex row above
      // it. `transition-transform` rather than animating width, which would
      // reflow every screen behind it on each frame.
      className={cn(
        "fixed inset-y-0 left-0 z-40 flex h-screen w-sidebar shrink-0 flex-col bg-navy-900 text-white",
        "transition-transform duration-200 ease-out will-change-transform",
        "lg:static lg:z-auto lg:translate-x-0 lg:transition-none",
        // `invisible` is the accessibility half of "closed", and it has to be
        // there: a drawer that is only translated off-canvas is still in the
        // tab order and still in the accessibility tree, so a keyboard user
        // tabs into a menu nobody can see. `visibility: hidden` takes it out of
        // both, and `lg:visible` puts the static rail back.
        open
          ? "visible translate-x-0 shadow-pop"
          : "invisible -translate-x-full",
        "lg:visible",
      )}
    >
      {/* ── Logo lockup ─────────────────────────────────────────────────────── */}
      <div className="relative px-5 py-5">
        {/* Only while it is a drawer. The backdrop and Escape close it too, but
            a visible control is what somebody looks for first — and on a
            touchscreen there is no Escape key. */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("nav.close", "Close menu")}
          className="absolute right-3 top-3 grid size-touch place-items-center rounded-control text-white/70 hover:bg-navy-800 hover:text-white lg:hidden"
        >
          <X className="size-5" aria-hidden />
        </button>

        <Logo height={38} label="Jalusi SmartSpaza" />
        <p className="mt-2 truncate text-[0.6875rem] leading-tight text-white/45">
          {t("brand.tagline")}
        </p>
      </div>

      {/* ── Navigation ──────────────────────────────────────────────────────────
          The label belongs on the `nav` landmark, not on the `aside` that wraps
          it: landmark navigation announces "navigation: Main navigation", and an
          unnamed nav is indistinguishable from any other in a screen reader's
          landmark list.

          `min-h-0` is load-bearing, not decorative: a flex child defaults to
          `min-height: auto`, which refuses to shrink below its content. Without
          it the list overflows its track at 768px — the design floor — and runs
          underneath the summary panel instead of scrolling. */}
      <nav
        className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 pb-4"
        aria-label={t("nav.primary")}
      >
        <ul className="space-y-1">
          {visibleRoutes.map((route) => (
            <li key={route.path}>
              <NavLink
                to={route.path}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    "flex h-touch items-center gap-3 rounded-control px-3 text-sm transition-colors",
                    isActive
                      ? "bg-navy-800 font-semibold text-white"
                      : "font-medium text-white/70 hover:bg-navy-800/60 hover:text-white",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <route.icon
                      className={cn(
                        "size-5 shrink-0",
                        isActive ? "text-brand-500" : "text-white/60",
                      )}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="truncate">
                      {t(`nav.${route.label.toLowerCase()}`, route.label)}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* ── Contextual summary ──────────────────────────────────────────────── */}
      {active?.summary && (
        <div className="px-3 pb-3">
          <SidebarSummary kind={active.summary} />
        </div>
      )}

      {/* ── Logout ──────────────────────────────────────────────────────────── */}
      <div className="border-t border-white/10 px-3 py-3">
        <button
          type="button"
          onClick={() => void logout()}
          className="flex h-touch w-full items-center gap-3 rounded-control px-3 text-sm font-medium text-white/70 transition-colors hover:bg-navy-800 hover:text-white"
        >
          <LogOut
            className="size-5 shrink-0 text-white/60"
            strokeWidth={2}
            aria-hidden
          />
          {t("nav.logout")}
        </button>
      </div>
    </aside>
  );
}
